/**
 * Client-side navigation for the design-system links that render a plain
 * `<a href>` (`Sidebar.Item`, `Breadcrumb.Item`).
 *
 * The anchor keeps its real `href` (middle-click / ⌘-click / "open in new
 * tab" and screen readers behave as links), while a plain left click is
 * intercepted and routed through React Router — no full page reload, so the
 * session, the React Query cache and the brand theme survive navigation.
 */

import { useCallback, type MouseEvent } from 'react';
import { useNavigate } from 'react-router-dom';

/** `true` for a click the browser should handle itself (new tab, …). */
function isModifiedClick(event: MouseEvent): boolean {
  return (
    event.defaultPrevented ||
    event.button !== 0 ||
    event.metaKey ||
    event.ctrlKey ||
    event.shiftKey ||
    event.altKey
  );
}

/**
 * Returns a click handler factory: `onClick={spaLink('/extranet/x/contrats')}`.
 * The optional `after` callback runs after navigating (e.g. close a drawer).
 */
export function useSpaLink(): (to: string, after?: () => void) => (event: MouseEvent) => void {
  const navigate = useNavigate();
  return useCallback(
    (to: string, after?: () => void) => (event: MouseEvent) => {
      if (isModifiedClick(event)) return;
      event.preventDefault();
      void navigate(to);
      after?.();
    },
    [navigate],
  );
}
